import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Skeleton, Tooltip } from 'src/common';
import styles from './index.module.scss';
import { useBuyerById, useGetOrgs } from 'src/models/organisations/hooks';
import { OnVisibilityTrigger, useFetchMore } from 'src/common/infinityScroll';
import { DEFAULT_PAGE_SIZE } from 'src/models/organisations/types';
import classNames from 'classnames';

export interface LabeledValue {
  key: string;
  value: string;
  label: ReactNode;
}
interface Props {
  onSelect: (orgId: string) => void;
  selected?: string;
  className?: string;
}
export const OrgsSelect: FC<Props> = ({ onSelect, selected, className }) => {
  const { t } = useTranslation();
  const { data: buyer } = useBuyerById(selected);
  const selectedValue = useMemo(
    () =>
      buyer
        ? { value: buyer.organisationNumber, key: buyer.organisationNumber, label: buyer.organisationName }
        : undefined,
    [buyer]
  );
  const [searchPhrase, updateSearchPhrase] = useState('');
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const { data: orgs, loading, fetchMore, fetchingMore } = useGetOrgs(searchPhrase);
  const [searchFocus, setSearchFocus] = useState(false);

  const onSelectFn = useCallback(
    (value: LabeledValue) => {
      updateSearchPhrase('');
      onSelect(value.value);
    },
    [onSelect]
  );

  const onSearch = useCallback(
    value => {
      updateSearchPhrase(value);
    },
    [updateSearchPhrase]
  );

  const onFocus = useCallback(() => setSearchFocus(true), []);
  const onBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (!value) {
      setSearchFocus(false);
    }
  }, []);

  const isMore = useMemo(() => {
    return orgs.length % DEFAULT_PAGE_SIZE === 0 && orgs.length >= DEFAULT_PAGE_SIZE;
  }, [orgs.length]);

  const loadMore = useCallback(
    (variables: { resultOffset: number }) =>
      fetchMore({
        variables
      }),
    [fetchMore]
  );

  const onLoadMore = useFetchMore({ resultOffset: orgs.length }, loadMore);

  return (
    <Select
      placeholder={t('Tenders.BuyersFilter.searchBuyers')}
      showSearch
      labelInValue
      filterOption={false}
      onSearch={onSearch}
      searchValue={searchPhrase}
      onSelect={onSelectFn}
      value={selectedValue}
      className={classNames(styles.select, { [styles.isFocus]: searchFocus }, className)}
      onFocus={onFocus}
      onBlur={onBlur}
      virtual={true}
      dropdownRender={menu => (
        <div className={styles.dropdown} ref={setContainer}>
          {menu}
        </div>
      )}
      getPopupContainer={trigger => trigger.parentNode}
      notFoundContent={loading ? <Skeleton loading /> : null}
    >
      {orgs.map(({ organisationNumber, organisationName }) => (
        <Select.Option value={organisationNumber} key={organisationNumber} className={styles.option}>
          <Tooltip title={organisationName}>
            <span className={styles.item}>
              <span className={styles.itemValue}>{organisationName}</span>
              <span className={styles.itemKey}>{organisationNumber}</span>
            </span>
          </Tooltip>
        </Select.Option>
      ))}
      {isMore && (
        <Select.Option value={''}>
          <OnVisibilityTrigger
            isLoading={fetchingMore}
            onVisible={onLoadMore}
            container={container}
            style={{ display: 'block' }}
          >
            <Skeleton active loading className={styles.skeleton} paragraph />
          </OnVisibilityTrigger>
        </Select.Option>
      )}
    </Select>
  );
};

export default OrgsSelect;
